import type React from 'react'
import { type FunctionComponent, type ReactNode, useState, useRef } from 'react'

type Props = {
  items: ReactNode[]
  onOrderUpdated: (oldPosition: number, newPosition: number) => void
  labelledById: string
  isDisabled: boolean
}

export const DragDropList: FunctionComponent<Props> = ({ items, onOrderUpdated, labelledById, isDisabled }) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)
  const dragNodeRef = useRef<HTMLDivElement | null>(null)

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    if (isDisabled) {
      e.preventDefault()
      return
    }

    setDraggedIndex(index)
    dragNodeRef.current = e.currentTarget

    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', e.currentTarget.outerHTML)
  }

  const handleDragEnd = () => {
    if (isDisabled) return

    if (draggedIndex !== null && dropIndex !== null && draggedIndex !== dropIndex) {
      const actualDropIndex = Math.floor(dropIndex)
      onOrderUpdated(draggedIndex, actualDropIndex)
    }

    setDraggedIndex(null)
    setDropIndex(null)
    dragNodeRef.current = null
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (isDisabled) return

    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    if (isDisabled || draggedIndex === null) return

    e.preventDefault()

    if (index === draggedIndex) {
      setDropIndex(null)
      return
    }

    // Check if this would actually result in a position change
    const wouldChangePosition = draggedIndex < index ? index >= draggedIndex + 1 : index < draggedIndex

    if (wouldChangePosition) {
      if (draggedIndex < index) {
        // Dragging downward - show indicator at bottom
        setDropIndex(index + 0.5)
      } else {
        // Dragging upward - show indicator at top
        setDropIndex(index)
      }
    } else {
      setDropIndex(null)
    }
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (isDisabled) return

    // Check if we're leaving the container entirely
    const rect = e.currentTarget.getBoundingClientRect()
    const isOutside = e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom

    if (isOutside) {
      setDropIndex(null)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    if (isDisabled) return

    e.preventDefault()

    if (draggedIndex !== null) {
      setDropIndex(index)
    }
  }

  return (
    <div aria-labelledby={labelledById} onDragLeave={handleDragLeave}>
      {items.map((item, index) => (
        <div
          key={`dragdrop-item-${index}`}
          draggable={!isDisabled}
          onDragStart={e => handleDragStart(e, index)}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragEnter={e => handleDragEnter(e, index)}
          onDrop={e => handleDrop(e, index)}
          role="button"
          className={`
            transition-all duration-200 ease-in-out
            ${isDisabled ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'}
            ${draggedIndex === index ? 'opacity-50' : 'opacity-100'}
            ${dropIndex === index && draggedIndex !== null && draggedIndex !== index ? 'border-t-2 pt-0.5' : ''}
            ${dropIndex === index + 0.5 && draggedIndex !== null && draggedIndex !== index ? 'border-b-2 pb-0.5' : ''}
          `}
          style={{
            ...(dropIndex === index &&
              draggedIndex !== null &&
              draggedIndex !== index && {
                borderTopColor: 'var(--colors-green-light)'
              }),
            ...(dropIndex === index + 0.5 &&
              draggedIndex !== null &&
              draggedIndex !== index && {
                borderBottomColor: 'var(--colors-green-light)'
              })
          }}
        >
          {item}
        </div>
      ))}
    </div>
  )
}
